// DropdownControl.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Control } from '../ControlRenderer';
import { evaluateTemplate, get, set } from '../utils/Utils';
import { Select, MenuItem, FormControl, Typography, TextField, Checkbox, ListItemText, Autocomplete } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormModeType, useFormData } from '../../../components/FormDataProvider';
import { useValidation } from '@/components/ValidationProvider';
import { useAuth } from '@/components/AuthProvider';
import useActionHandler from '@/hooks/useActionHandler';
import ModalBox, { Action } from '../ModalBox';
import ControlIcons from '../ControlIcons';

/**
 * Represents a dropdown control with properties to manage its source items,
 * selection mode, and autocomplete feature. Extends the base Control interface.
 */
export interface DropdownControl extends Control {
  /**
   * A string used as a key to identify the list of items associated with this dropdown.
   * It helps in rendering or fetching the dropdown's data source.
   */
  itemsKey: string;

  /**
   * Represents the actual items that populate the dropdown.
   * This can be any enumerable set that the dropdown will iterate over to display available options.
   */
  items: any;

  /**
   * Allows support for multi-selection within the dropdown.
   * If true, users can select multiple items simultaneously. (Optional)
   */
  multiple?: boolean;

  /**
   * Switches the dropdown to autocomplete mode, enabling users to search and select
   * items based on partial input matches. This enhances usability for dropdowns with large datasets. (Optional)
   */
  autocomplete?: boolean;

  /** Allows the user to enter a custom value that is not in the list */
  freeSolo?: boolean;

  /**
   * Allows to set null value in the dropdown list
   * If true, an empty item is added to the dropdown list to be able to select null value. (Optional)
   */
  nullable?: boolean;

  /**
   * Allows to specify a placeholder string in the dropdown control (Optional)
   */
  placeholder?: string;

  /**
   * Allows to display codes along with the values (Optional)
   */
  displayCodes?: boolean;

  /**
   * Action to be executed when the value of the dropdown changes (Optional)
   */
  changeAction?: Action;
  /**
   * If true, the first item in the dropdown will be automatically selected
   * when the control is initialized. (Optional)
   */
  autoSelectFirst?: boolean;
}

interface DropdownControlProps {
  control: Control;
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
}

const DropdownControl: React.FC<DropdownControlProps> = ({ control, data, onDataChange, mode }) => {
  const { executeAction, UIAction, handleSuccess, handleFailure, handleClose } = useActionHandler();
  const { errors, rule, validateData, setDataContext } = useValidation();
  const theme = useTheme();
  const { token } = useAuth();
  const handleFieldChange = (fieldName: string, value: string | string[] | null) => {
    // Handle Select All/Deselect All for multiple select
    if (multiple && Array.isArray(value)) {
      const actualOptions = options.filter((opt: any[]) => opt[0] !== '__SELECT_ALL__');
      const selectAllIndex = value.indexOf('__SELECT_ALL__');

      if (selectAllIndex !== -1) {
        // Select All was clicked
        const currentSelections = Array.isArray(selectedValue) ? selectedValue.filter((val: any) => val !== '__SELECT_ALL__') : [];
        const allActualValues = actualOptions.map((opt: any[]) => opt[0]);

        // If all items are selected, deselect all; otherwise select all
        if (currentSelections.length === allActualValues.length) {
          value = []; // Deselect all
        } else {
          value = allActualValues; // Select all actual options
        }
      } else {
        // Regular selection, filter out Select All if it was previously selected
        value = value.filter((val: any) => val !== '__SELECT_ALL__');
      }
    }

    setSelectedValue(value);
    if (value === '') {
      set(data, fieldName, null);
      onDataChange(null);
    }
    else {
      set(data, fieldName, value);
      onDataChange(data);
    }
    validateData();

    const dropdown = control as DropdownControl;
    if (dropdown.changeAction) {
      executeAction(dropdown.changeAction, data);
    }
  };

  const { formData, prepareLookupData } = useFormData();
  const multiple = (control as DropdownControl).multiple ?? false;
  const autocomplete = (control as DropdownControl).autocomplete ?? false;
  const freeSolo = (control as DropdownControl).freeSolo ?? false;

  const [selectedValue, setSelectedValue] = useState<any>(multiple ? [] : '');
  const [inputValue, setInputValue] = useState('');

  const errorMessage = errors && control.dataKey ? errors[control.dataKey] : undefined;

  const hasControlIcons = !!control.helpText || !!control.helpLink || !!errorMessage;

  const dropdownControl = control as DropdownControl;

  useEffect(() => {
    const getInitialSelectedValue = () => {
      let currentValue = get(data, control.dataKey ?? '');
      if (currentValue === undefined) currentValue = null;
      return multiple
        ? (Array.isArray(currentValue) ? currentValue : [])
        : (Array.isArray(currentValue) ? '' : currentValue);
    };

    const initial = getInitialSelectedValue();
    setSelectedValue(initial);
    if (!multiple && typeof initial === 'string') {
      setInputValue(initial);
    }
  }, [control.dataKey, data, multiple]);

  useEffect(() => {
    if (data) {
      setDataContext(data);
    }
  }, [data, setDataContext]);

  const itemsKey = useMemo(() => {
    if (dropdownControl.itemsKey) {
      return evaluateTemplate(dropdownControl.itemsKey, data, formData);
    }
    return null;
  }, [dropdownControl.itemsKey, data, formData]);

  useEffect(() => {
    // we need to wait for token before retrieving the lookups
    if (itemsKey && token) {
      prepareLookupData(itemsKey, "", undefined);
    }
  }, [token, itemsKey, prepareLookupData]);

  let options = dropdownControl.items;
  let optionsFromFormData = get(formData, itemsKey ?? '', null);
  if (Array.isArray(optionsFromFormData) && optionsFromFormData.length > 2) {
    // Sort options alphabetically case-insensitive based on display label
    optionsFromFormData = optionsFromFormData.slice().sort((a: any, b: any) => a[1].toString().localeCompare(b[1].toString(), undefined, { sensitivity: 'base' }));
  }
  if (Array.isArray(options)) {
    if (optionsFromFormData) {
      options = options.concat(optionsFromFormData);
    }
  } else if (optionsFromFormData) {
    options = optionsFromFormData;
  } else {
    options = [];
  }
  let currentMode = control.mode ? control.mode : mode;
  const className = `eform-text-${currentMode === 'view' ? 'readonly-' : ''}${theme.palette.mode}`;
  const minWidthStyle = control.minWidth ? { minWidth: control.minWidth } : { minWidth: '120px' };

  if (typeof options === 'string') {
    // in case of plain text this is an info message
    return (<TextField
      variant="standard"
      type="text"
      className={className}
      fullWidth
      defaultValue={options}
      inputProps={{ readOnly: true }}
      style={minWidthStyle}
    />)
  }

  // Add a nullable option if specified (but not for multiple select)
  if (dropdownControl.nullable && !multiple) {
    options = [[null, '']].concat(options); // Append an empty string for display purposes
  }

  // Sort options alphabetically case-insensitive based on display label
  const sortedOptions = options.slice().sort((a: any, b: any) => a[1].toString().localeCompare(b[1].toString(), undefined, { sensitivity: 'base' }));

  // For multiple select, add "Select All" option at the top
  if (multiple) {
    const selectAllOption = ['__SELECT_ALL__', 'Select All'];
    options = [selectAllOption].concat(sortedOptions);
  } else {
    options = sortedOptions;
  }

  // Function to get value by key
  const getValueByKey = (key: any) => {
    //console.log('DropdownControl.getValueByKey');
    //console.log(options);
    //console.log(key);
    if (key === null) return ''; // Return an empty string for null
    for (let i = 0; i < options.length; i++) {
      if (options[i][0] === key) {
        return options[i][1]; // Return the value corresponding to the key
      }
    }
    return null; // Return null if key is not found
  };

  // Function to get display value for multiple selection
  const getDisplayValues = (keys: string[]) => {
    return keys.map((key) => getValueByKey(key)).join(', ');
  };

  if (currentMode === 'label') {
    return (
      <Typography className='eform-labelmode'>
        {multiple && Array.isArray(selectedValue) ? getDisplayValues(selectedValue) : getValueByKey(selectedValue)}
      </Typography>
    )
  }

  const commonAutocompleteProps = {
    multiple,
    className: `eform-dropdown ${className}`,
    clearOnEscape: true,
    options, // [[key, label], ...]
    getOptionLabel: (option: any) =>
      Array.isArray(option) ? (option[1] ?? '') : (option ?? ''),
    value: multiple
      ? options.filter(
        (option: any[]) =>
          selectedValue?.includes(option[0]) && option[0] !== '__SELECT_ALL__'
      )
      : options.find((option: any[]) => option[0] === selectedValue) ??
      (selectedValue ?? ''),
    renderInput: (params: any) => (
      <TextField
        {...params}
        placeholder={dropdownControl.placeholder}
        label={control.label}
        variant="standard"
        style={minWidthStyle}
      />
    ),
    style: minWidthStyle,
  };

  // When freeSolo = false → use original behavior (no inputValue/onInputChange)
  const autocompleteProps = !freeSolo
    ? {
      ...commonAutocompleteProps,
      onChange: (event: any, newValue: any) =>
        handleFieldChange(
          control.dataKey ?? '',
          multiple
            ? (newValue as any[]).map((item) => item[0])
            : newValue
              ? newValue[0]
              : ''
        )
    }
    : {
      // freeSolo = true → extend with controlled input + string support
      ...commonAutocompleteProps,
      freeSolo,
      inputValue,
      onChange: (event: any, newValue: any) => {
        if (multiple) {
          const values = (newValue as any[]).map((item) =>
            Array.isArray(item) ? item[0] : item
          );
          handleFieldChange(control.dataKey ?? '', values);
        } else {
          let value: any = null;
          if (Array.isArray(newValue)) value = newValue[0];
          else if (typeof newValue === 'string') value = newValue; // arbitrary string
          else if (newValue) value = (newValue as any)[0];
          else value = '';
          handleFieldChange(control.dataKey ?? '', value);
        }
      },
      onInputChange: (event: any, newInputValue: string) => {
        setInputValue(newInputValue);
        // keep data in sync while typing for single-select freeSolo
        if (!multiple) {
          handleFieldChange(control.dataKey ?? '', newInputValue);
        }
      }
    };

  return (
    <div key={control.name} style={{ ...minWidthStyle }}>
      {currentMode !== 'view' ? (
        options && autocomplete ? (
          hasControlIcons ? (
            <FormControl variant="standard" className="eform-dropdown" error={!!errorMessage}>
              <Autocomplete {...autocompleteProps} />
              <ControlIcons control={control} errorMessage={errorMessage} offsetRight={15} />
            </FormControl>
          ) : (
            <Autocomplete {...autocompleteProps} />
          )
        ) : (
          errorMessage ? (
            <FormControl variant="standard" className="eform-dropdown" error={!!errorMessage}><Select
              multiple={multiple}
              className={`eform-dropdown ${className}`}
              MenuProps={{ disableScrollLock: true }}
              label={control.label}
              error={!!errorMessage}
              name={control.name}
              value={selectedValue}
              onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value as string | string[])}
              renderValue={(selected) => multiple && Array.isArray(selected) ? getDisplayValues(selected) : getValueByKey(selected)}
              style={minWidthStyle}
            >
              {options && options.map((option: Array<any>, index: React.Key | null | undefined) => {
                const isSelectAll = option[0] === '__SELECT_ALL__';
                const actualOptions = options.filter((opt: any[]) => opt[0] !== '__SELECT_ALL__');
                const currentSelections = Array.isArray(selectedValue) ? selectedValue.filter((val: any) => val !== '__SELECT_ALL__') : [];
                const allSelected = currentSelections.length === actualOptions.length && actualOptions.length > 0;

                return (
                  <MenuItem key={index} value={option[0]} className="eform-dropdown-menuitem">
                    {multiple && <Checkbox checked={isSelectAll ? allSelected : (Array.isArray(selectedValue) && selectedValue.includes(option[0]))} />}
                    <ListItemText primary={option[1]} />
                  </MenuItem>
                );
              })}
            </Select>
              <ControlIcons control={control} errorMessage={errorMessage} offsetRight={15} />
            </FormControl>
          ) : (
            <Select
              multiple={multiple}
              className={`eform-dropdown ${className}`}
              MenuProps={{ disableScrollLock: true }}
              label={control.label}
              name={control.name}
              value={selectedValue}
              onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value as string | string[])}
              renderValue={(selected) => multiple && Array.isArray(selected) ? getDisplayValues(selected) : getValueByKey(selected)}
              style={minWidthStyle}
            >
              {options && options.map((option: Array<any>, index: React.Key | null | undefined) => {
                const isSelectAll = option[0] === '__SELECT_ALL__';
                const actualOptions = options.filter((opt: any[]) => opt[0] !== '__SELECT_ALL__');
                const currentSelections = Array.isArray(selectedValue) ? selectedValue.filter((val: any) => val !== '__SELECT_ALL__') : [];
                const allSelected = currentSelections.length === actualOptions.length && actualOptions.length > 0;

                return (
                  <MenuItem key={index} value={option[0]} className="eform-dropdown-menuitem">
                    {multiple && <Checkbox checked={isSelectAll ? allSelected : (Array.isArray(selectedValue) && selectedValue.includes(option[0]))} />}
                    <ListItemText primary={option[1]} />
                  </MenuItem>
                );
              })}
            </Select>
          ))
      ) : (
        <TextField
          variant="standard"
          type="text"
          className={className}
          fullWidth
          defaultValue={multiple && Array.isArray(selectedValue) ? getDisplayValues(selectedValue) : getValueByKey(selectedValue) ?? ''}
          inputProps={{ readOnly: true }}
          style={minWidthStyle}
        />
      )}
      {UIAction &&
        <ModalBox
          action={UIAction}
          data={data}
          onDataChange={() => { }}
          mode={mode}
          onComplete={() => handleSuccess(UIAction, data)}
          onError={() => handleFailure(UIAction, data)}
          onClose={() => handleClose(UIAction, data)} />}
    </div>
  );
};

export default DropdownControl;
